import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

export const saraTheme = definePreset(Aura, {
    // PrimeFlex blue utilities share the pre-migration palette across all screens.
    primitive: {
        red: { 50: '#fff5f5', 100: '#ffd1ce', 200: '#ffada7', 300: '#ff8980', 400: '#ff6459', 500: '#ff4032', 600: '#d9362b', 700: '#b32d23', 800: '#8c231c', 900: '#661a14' },
        green: { 50: '#f6fbf6', 100: '#d4ecd5', 200: '#b2ddb4', 300: '#90cd93', 400: '#6ebe71', 500: '#4caf50', 600: '#419544', 700: '#357b38', 800: '#2a602c', 900: '#1e4620' },
        blue: {
        50: '#f4fafe', 100: '#cae6fc', 200: '#a0d2fa', 300: '#75bef8',
        400: '#4baaf5', 500: '#2196f3', 600: '#1c80cf', 700: '#1769aa',
        800: '#125386', 900: '#0d3c61'
    } },
    semantic: { primary: {
        // Retain the legacy ramp for PrimeFlex text-primary-* and other consumers.
        50: '#f4fafe', 100: '#cae6fc', 200: '#a0d2fa', 300: '#75bef8',
        400: '#4baaf5', 500: '#2196f3', 600: '#1c80cf', 700: '#1769aa',
        800: '#125386', 900: '#0d3c61',
        950: '{blue.950}'
    }, colorScheme: { light: {
        surface: { 0: '#ffffff', 50: '#fafafa', 100: '#f5f5f5', 200: '#eeeeee', 300: '#e0e0e0', 400: '#bdbdbd', 500: '#9e9e9e', 600: '#757575', 700: '#616161', 800: '#424242', 900: '#212121' },
        text: { color: '#495057', hoverColor: '#495057', mutedColor: '#6c757d', hoverMutedColor: '#6c757d' },
        primary: {
        color: '#2196f3', contrastColor: '#ffffff',
        hoverColor: '#0b7ad1', activeColor: '#0967af'
    } } } },
    components: {
        toast: {
            root: { borderRadius: '3px', borderWidth: '0 0 0 6px' },
            icon: { size: '2rem' },
            content: { padding: '1rem', gap: '1rem' },
            text: { gap: '0' },
            summary: { fontWeight: '700', fontSize: '1rem' },
            detail: { fontWeight: '400', fontSize: '1rem' },
            closeButton: { width: '2rem', height: '2rem' },
            colorScheme: { light: {
                root: { blur: '0' },
                info: { background: '#b3e5fc', borderColor: '#0891cf', color: '#044868',
                    detailColor: '#044868', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                success: { background: '#c8e6c9', borderColor: '#439446', color: '#224a23',
                    detailColor: '#224a23', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                warn: { background: '#ffecb3', borderColor: '#d9a300', color: '#6d5100',
                    detailColor: '#6d5100', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                error: { background: '#ffd1ce', borderColor: '#d9362b', color: '#73000c',
                    detailColor: '#73000c', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } }
            } }
        },
        message: {
            colorScheme: { light: { info: {
                color: '#044868',
                outlined: { color: '#044868' },
                simple: { color: '#044868' }
            } } }
        },
        button: {
            root: { label: { fontWeight: 'inherit' } },
            colorScheme: { light: {
                // Saga Blue used a darker blue for link actions such as Show full address.
                link: { color: '#0b7ad1', hoverColor: '#0b7ad1', activeColor: '#0b7ad1' }
            } }
        },
        select: {
            root: {
                background: '#ffffff', borderColor: '#ced4da',
                hoverBorderColor: '#2196f3', focusBorderColor: '#2196f3',
                color: '#495057', placeholderColor: '#6c757d', shadow: 'none'
            },
            dropdown: { color: '#6c757d' }
        },
        toggleswitch: {
            colorScheme: { light: {
                handle: { disabledBackground: '#ffffff' }
            } }
        },
        radiobutton: {
            root: {
                width: '20px', height: '20px', background: '#ffffff',
                borderColor: '#ced4da', hoverBorderColor: '#2196f3',
                focusBorderColor: '#2196f3', checkedBorderColor: '#2196f3',
                checkedHoverBorderColor: '#0b7ad1', checkedFocusBorderColor: '#2196f3',
                checkedBackground: 'transparent', checkedHoverBackground: 'transparent',
                disabledBackground: '#ffffff', checkedDisabledBorderColor: '#ced4da',
                invalidBorderColor: '#f44336', shadow: 'none',
                focusRing: { width: '0', shadow: '0 0 0 0.2rem #a6d5fa' }
            },
            icon: {
                size: '12px', checkedColor: '#237ec0',
                checkedHoverColor: '#237ec0', disabledColor: '#237ec0'
            }
        },
        accordion: {
            panel: { borderWidth: '0', borderColor: '#dee2e6' },
            header: {
                padding: '1rem', fontWeight: '600', borderWidth: '1px',
                borderColor: '#dee2e6', borderRadius: '3px',
                color: '#495057', hoverColor: '#495057',
                activeColor: '#495057', activeHoverColor: '#495057',
                background: '#f8f9fa', hoverBackground: '#e9ecef',
                activeBackground: '#f8f9fa', activeHoverBackground: '#e9ecef',
                first: { topBorderRadius: '10px', borderWidth: '1px' },
                last: { bottomBorderRadius: '3px', activeBottomBorderRadius: '0' },
                toggleIcon: {
                    color: '#495057', hoverColor: '#495057',
                    activeColor: '#495057', activeHoverColor: '#495057'
                },
                focusRing: { width: '0', shadow: 'inset 0 0 0 0.2rem #a6d5fa' }
            },
            content: {
                padding: '1rem', borderWidth: '0 1px 1px',
                borderColor: '#dee2e6', background: '#ffffff', color: '#495057'
            }
        },
        badge: {
            root: { borderRadius: '10px' },
            colorScheme: {
                light: {
                    // Preserve the status palette from the previous Saga Blue theme.
                    success: { background: '#689f38', color: '#ffffff' },
                    danger: { background: '#d32f2f', color: '#ffffff' },
                    info: { background: '#0288d1', color: '#ffffff' }
                }
            }
        }
    }
})
